import React from 'react';
import { alpha, Backdrop, Fade, Modal, useTheme } from '@mui/material';
import { QcsTypography } from './basic/QcsTypography';
import { RepairLineBreaks } from './RepairLineBreaks';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  ModalCard,
  IconContainer,
  IconCircle,
  ContentContainer,
  TextContainer,
  OkButton,
} from './GeneralInfoModalStyles';

type ModalVariant = 'info' | 'warning' | 'error';

interface GeneralInfoModalProps {
  open: boolean;
  onClose: () => void;
  text: React.ReactNode;
  title?: string;
  variant?: ModalVariant;
}

const variantConfig = {
  info: {
    Icon: InfoOutlinedIcon,
    colorKey: 'info' as const,
  },
  warning: {
    Icon: WarningAmberRoundedIcon,
    colorKey: 'warning' as const,
  },
  error: {
    Icon: ErrorOutlineIcon,
    colorKey: 'error' as const,
  },
};

export const GeneralInfoModal: React.FC<GeneralInfoModalProps> = ({ open, onClose, text, title, variant }) => {
  const theme = useTheme();
  const config = variant ? variantConfig[variant] : null;
  const variantColor = config ? theme.palette[config.colorKey].main : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 300,
          sx: { backdropFilter: 'blur(4px)', backgroundColor: alpha(theme.palette.common.black, 0.3) },
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <ModalCard>
          {config && (
            <IconContainer>
              <IconCircle variantColor={variantColor!}>
                <config.Icon sx={{ fontSize: 30, color: variantColor }} />
              </IconCircle>
            </IconContainer>
          )}

          <ContentContainer style={{ paddingTop: config ? 20 : 28 }}>
            {title && (
              <QcsTypography
                variant="h6"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  textAlign: config ? 'center' : undefined,
                }}
              >
                {title}
              </QcsTypography>
            )}

            <TextContainer>
              <QcsTypography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-line',
                  lineHeight: 1.7,
                  color: theme.palette.text.secondary,
                  textAlign: config ? 'center' : undefined,
                  fontSize: '0.925rem',
                }}
              >
                {typeof text === 'string' ? <RepairLineBreaks text={text} /> : text}
              </QcsTypography>
            </TextContainer>

            <OkButton
              onClick={onClose}
              variant="contained"
              color={config ? config.colorKey : 'primary'}
              fullWidth
              disableElevation
            >
              OK
            </OkButton>
          </ContentContainer>
        </ModalCard>
      </Fade>
    </Modal>
  );
};
